import type { PostHog } from 'posthog-js'

// posthog-js is ~94KB gzipped, the largest chunk the landing page would otherwise ship. Every
// client caller goes through loadPostHog(), so it's code-split and initialized once, on idle
// (instrumentation-client.ts) or on the first capture, whichever comes first. Calls made before
// it loads wait on the same promise, so they still land, in order.

let client: Promise<PostHog | null> | undefined

export function loadPostHog(): Promise<PostHog | null> {
  if (typeof window === 'undefined') return Promise.resolve(null)
  client ??= import('posthog-js')
    .then(({ default: posthog }) => {
      const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
      if (!key) return null
      if (!posthog.__loaded) {
        posthog.init(key, {
          api_host: '/ingest',
          ui_host: 'https://us.posthog.com',
          defaults: '2026-01-30',
          capture_exceptions: true,
          disable_session_recording: true,
          debug: process.env.NODE_ENV === 'development',
        })
      }
      return posthog
    })
    .catch(() => null)
  return client
}

/** Runs `fn` with PostHog once it's loaded. Telemetry never throws into the app. */
export function withPostHog(fn: (posthog: PostHog) => void) {
  void loadPostHog().then((posthog) => {
    if (!posthog) return
    try {
      fn(posthog)
    } catch {
      // PostHog blocked or misbehaving: never break the app
    }
  })
}

/** Runs `fn` when the main thread is idle (setTimeout where requestIdleCallback is missing, e.g. Safari). Returns a cancel function. */
export function whenIdle(fn: () => void, timeout: number): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(fn, { timeout })
    return () => window.cancelIdleCallback(id)
  }
  const timer = window.setTimeout(fn, Math.min(timeout, 2000))
  return () => window.clearTimeout(timer)
}
